import {
  CanvassingSessionCalendarPreset,
  CanvassingSessionCaptureStatusFilter,
  CanvassingSessionListSort,
  CanvassingSessionProcessingStatus,
} from '../../graphql/__generated__/graphql';
import {
  getCanvassingSessionCaptureStatusFilterLabel,
  getCanvassingSessionListSortLabel,
  getCanvassingSessionProcessingStatusLabel,
} from './canvassingSessionLabels';

export type CanvassingSessionDateRangePreset = 'all' | 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom';

export const DATE_RANGE_OPTIONS: ReadonlyArray<{ value: CanvassingSessionDateRangePreset; label: string }> = [
  { value: 'all', label: 'All time' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom' },
];

export const CANVASSING_SESSION_CALENDAR_PRESETS: Record<
  Exclude<CanvassingSessionDateRangePreset, 'custom'>,
  CanvassingSessionCalendarPreset
> = {
  all: CanvassingSessionCalendarPreset.All,
  last_7_days: CanvassingSessionCalendarPreset.Last_7Days,
  last_30_days: CanvassingSessionCalendarPreset.Last_30Days,
  last_90_days: CanvassingSessionCalendarPreset.Last_90Days,
};

export type CanvassingSessionSort = 'newest' | 'oldest';

export const CANVASSING_SESSION_SORT_OPTIONS: ReadonlyArray<{ value: CanvassingSessionSort; label: string }> = [
  { value: 'newest', label: getCanvassingSessionListSortLabel(CanvassingSessionListSort.Newest) },
  { value: 'oldest', label: getCanvassingSessionListSortLabel(CanvassingSessionListSort.Oldest) },
];

export const CANVASSING_SESSION_CAPTURE_STATUS_OPTIONS: ReadonlyArray<{
  value: CanvassingSessionCaptureStatusFilter;
  label: string;
}> = [
  {
    value: CanvassingSessionCaptureStatusFilter.Active,
    label: getCanvassingSessionCaptureStatusFilterLabel(CanvassingSessionCaptureStatusFilter.Active),
  },
  {
    value: CanvassingSessionCaptureStatusFilter.Ended,
    label: getCanvassingSessionCaptureStatusFilterLabel(CanvassingSessionCaptureStatusFilter.Ended),
  },
];

export const CANVASSING_SESSION_PROCESSING_STATUS_OPTIONS: ReadonlyArray<{
  value: CanvassingSessionProcessingStatus;
  label: string;
}> = [
  {
    value: CanvassingSessionProcessingStatus.Active,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.Active),
  },
  {
    value: CanvassingSessionProcessingStatus.AudioPending,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.AudioPending),
  },
  {
    value: CanvassingSessionProcessingStatus.Failed,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.Failed),
  },
  {
    value: CanvassingSessionProcessingStatus.Merged,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.Merged),
  },
  {
    value: CanvassingSessionProcessingStatus.Partial,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.Partial),
  },
  {
    value: CanvassingSessionProcessingStatus.Segmented,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.Segmented),
  },
  {
    value: CanvassingSessionProcessingStatus.Submitted,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.Submitted),
  },
  {
    value: CanvassingSessionProcessingStatus.Unknown,
    label: getCanvassingSessionProcessingStatusLabel(CanvassingSessionProcessingStatus.Unknown),
  },
];

export type CanvassingSessionFilterState = {
  dateRange: CanvassingSessionDateRangePreset;
  fromDate?: string;
  throughDate?: string;
  canvasserId: string | null;
  captureStatus: CanvassingSessionCaptureStatusFilter | null;
  processingStatus: CanvassingSessionProcessingStatus | null;
  sort: CanvassingSessionSort;
};

export const DEFAULT_CANVASSING_SESSION_FILTERS: CanvassingSessionFilterState = {
  dateRange: 'all',
  canvasserId: null,
  captureStatus: null,
  processingStatus: null,
  sort: 'newest',
};

export type CanvassingSessionCustomDateErrors = {
  fromDate: string | null;
  throughDate: string | null;
};

const isCalendarDate = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const getCanvassingSessionCustomDateErrors = (
  fromDate: string | undefined,
  throughDate: string | undefined,
): CanvassingSessionCustomDateErrors => {
  const errors: CanvassingSessionCustomDateErrors = {
    fromDate: !fromDate ? 'From date is required.' : !isCalendarDate(fromDate) ? 'Enter a valid calendar date.' : null,
    throughDate: !throughDate
      ? 'Through date is required.'
      : !isCalendarDate(throughDate)
        ? 'Enter a valid calendar date.'
        : null,
  };

  if (
    errors.fromDate === null &&
    errors.throughDate === null &&
    fromDate !== undefined &&
    throughDate !== undefined &&
    fromDate > throughDate
  ) {
    errors.fromDate = 'From date must be on or before Through date.';
  }

  return errors;
};

export const isCanvassingSessionCustomDateRangeValid = (
  fromDate: string | undefined,
  throughDate: string | undefined,
): boolean => {
  const errors = getCanvassingSessionCustomDateErrors(fromDate, throughDate);
  return errors.fromDate === null && errors.throughDate === null;
};

const SEARCH_PARAM_KEYS = {
  dateRange: 'range',
  canvasserId: 'canvasser',
  captureStatus: 'captureStatus',
  processingStatus: 'processingStatus',
  sort: 'sort',
} as const;

const isDateRangePreset = (value: string): value is CanvassingSessionDateRangePreset =>
  DATE_RANGE_OPTIONS.some(option => option.value === value);

const isSort = (value: string): value is CanvassingSessionSort =>
  CANVASSING_SESSION_SORT_OPTIONS.some(option => option.value === value);

export const parseCanvassingSessionCaptureStatus = (value: string): CanvassingSessionCaptureStatusFilter | null =>
  CANVASSING_SESSION_CAPTURE_STATUS_OPTIONS.find(option => option.value === value)?.value ?? null;

export const parseCanvassingSessionProcessingStatus = (value: string): CanvassingSessionProcessingStatus | null =>
  CANVASSING_SESSION_PROCESSING_STATUS_OPTIONS.find(option => option.value === value)?.value ?? null;

export const parseCanvassingSessionFilters = (searchParams: URLSearchParams): CanvassingSessionFilterState => {
  const range = searchParams.get(SEARCH_PARAM_KEYS.dateRange);
  const canvasserId = searchParams.get(SEARCH_PARAM_KEYS.canvasserId);
  const captureStatus = searchParams.get(SEARCH_PARAM_KEYS.captureStatus);
  const processingStatus = searchParams.get(SEARCH_PARAM_KEYS.processingStatus);
  const sort = searchParams.get(SEARCH_PARAM_KEYS.sort);
  const fromDate = searchParams.get('from') ?? undefined;
  const throughDate = searchParams.get('through') ?? undefined;

  return {
    dateRange: range && isDateRangePreset(range) ? range : DEFAULT_CANVASSING_SESSION_FILTERS.dateRange,
    ...(range === 'custom' ? { fromDate, throughDate } : {}),
    canvasserId: canvasserId || null,
    captureStatus: captureStatus ? parseCanvassingSessionCaptureStatus(captureStatus) : null,
    processingStatus: processingStatus ? parseCanvassingSessionProcessingStatus(processingStatus) : null,
    sort: sort && isSort(sort) ? sort : DEFAULT_CANVASSING_SESSION_FILTERS.sort,
  };
};

export const canvassingSessionFiltersToSearchParams = (filters: CanvassingSessionFilterState): URLSearchParams => {
  const params = new URLSearchParams();

  if (filters.dateRange !== DEFAULT_CANVASSING_SESSION_FILTERS.dateRange) {
    params.set(SEARCH_PARAM_KEYS.dateRange, filters.dateRange);
  }
  if (filters.dateRange === 'custom') {
    if (filters.fromDate !== undefined) params.set('from', filters.fromDate);
    if (filters.throughDate !== undefined) params.set('through', filters.throughDate);
  }
  if (filters.canvasserId) params.set(SEARCH_PARAM_KEYS.canvasserId, filters.canvasserId);
  if (filters.captureStatus) params.set(SEARCH_PARAM_KEYS.captureStatus, filters.captureStatus);
  if (filters.processingStatus) params.set(SEARCH_PARAM_KEYS.processingStatus, filters.processingStatus);
  if (filters.sort !== DEFAULT_CANVASSING_SESSION_FILTERS.sort) params.set(SEARCH_PARAM_KEYS.sort, filters.sort);

  return params;
};
